import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertTriangleIcon, BadgeCheckIcon, CalendarIcon, CheckIcon, ClipboardListIcon, ImageIcon, InfoIcon, MegaphoneIcon, MessageSquareIcon, UsersIcon, VoteIcon, XIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { Meter } from '../../components/ui/Meter';
import { announcements, communityEvents, communityMembers, communityMetrics, communityPosts, communityProfile, polls, registrations, type MemberState } from '../../data/communityConsole';

/**
 * The Community workspace.
 *
 * One page per section, routed by :section, matching how the other operator
 * consoles work. The overview answers "who is waiting on this society" rather
 * than restating totals that already sit in the sidebar.
 */

const MEMBER_TONE: Record<MemberState, 'ok' | 'warn' | 'danger' | 'neutral'> = {
  active: 'ok',
  pending: 'warn',
  invited: 'neutral',
  suspended: 'danger'
};
export function CommunityConsole() {
  const {
    section
  } = useParams();
  switch (section) {
    case 'members':
      return <Members data-id="element-7897" data-mp="im2kx69mdy2d" />;
    case 'posts':
      return <Posts data-id="element-7898" data-mp="mmlasurycu1x" />;
    case 'announcements':
      return <Announcements data-id="element-7899" data-mp="mltf7jycuwbn" />;
    case 'events':
      return <Events data-id="element-7900" data-mp="rl640a8sedgv" />;
    case 'registrations':
      return <Registrations data-id="element-7901" data-mp="rjuzu9bfj3p8" />;
    case 'polls':
      return <Polls data-id="element-7902" data-mp="wksien0cpzr9" />;
    case 'media':
      return <Media data-id="element-7903" data-mp="wlu37j7660m6" />;
    case 'settings':
      return <Settings data-id="element-7904" data-mp="11jluqc3wjkpu" />;
    default:
      return <Overview data-id="element-7905" data-mp="11ikja6iz4w7l" />;
  }
}

/* ----------------------------------------------------------------- overview */

function Overview() {
  const pending = communityMembers.filter(m => m.state === 'pending');
  const review = communityPosts.filter(p => p.state === 'needs review');
  const draftEvents = communityEvents.filter(e => e.state === 'draft');
  const nothingWaiting = pending.length + review.length + draftEvents.length === 0;
  return <>
      <PageHeader title={communityProfile.name} lead={`${communityProfile.kind} · ${communityProfile.institution}`} data-id="element-7906" data-mp="2qdkeg5i8bh1q" />

      <DemoNote data-id="element-7907" data-mp="30947mk5es1ms" />

      {/* Attention first: who is waiting on this society. */}
      <Panel title="What needs you" className="mt-6" data-id="element-7908" data-mp="3e8viic0mp1wk">
        {nothingWaiting ? <EmptyState icon={<CheckIcon className="h-5 w-5" data-id="element-7910" data-mp="39au07tb116m5" />} title="Nothing is blocked on you" description="No join requests, no reported posts and no unpublished events. Come back when something arrives." data-id="element-7909" data-mp="39a23gf5p12ua" /> : <ul className="divide-y divide-line" data-id="element-7911" data-mp="3j0n9gnnp59n8">
            {pending.map(member => <li key={member.id} className="flex flex-wrap items-center gap-3 py-3" data-id="element-7912" data-mp="us3bcld67p5">
                <UsersIcon aria-hidden className="h-5 w-5 shrink-0 text-warn-700" data-id="element-7913" data-mp="3szch7nlvpdpr" />
                <span className="min-w-0 flex-1" data-id="element-7914" data-mp="fgpiizrgy9kk">
                  <span className="block text-body font-medium text-ink" data-id="element-7915" data-mp="feuz5pvuipil">
                    {member.name} asked to join
                  </span>
                  <span className="block text-caption text-ink-muted" data-id="element-7916" data-mp="6h2e053g0nna">
                    {member.handle} · {member.programme} · {member.standing}
                  </span>
                </span>
                <span className="flex gap-2" data-id="element-7917" data-mp="3svls00oia0t8">
                  <Button size="sm" variant="primary" data-id="element-7918" data-mp="3str72md8i8p5">
                    Approve
                  </Button>
                  <Button size="sm" variant="secondary" data-id="element-7919" data-mp="1j0xnrxej2fi">
                    Decline
                  </Button>
                </span>
              </li>)}
            {review.map(post => <li key={post.id} className="flex flex-wrap items-center gap-3 py-3" data-id="element-7920" data-mp="3nuej1yammvkz">
                <AlertTriangleIcon aria-hidden className="h-5 w-5 shrink-0 text-danger-700" data-id="element-7921" data-mp="39z2r1ynuvj7r" />
                <span className="min-w-0 flex-1" data-id="element-7922" data-mp="3evb496iht2ca">
                  <span className="block text-body font-medium text-ink" data-id="element-7923" data-mp="l4cokpdut2au">
                    A post was reported and is waiting on a decision
                  </span>
                  <span className="block text-caption text-ink-muted" data-id="element-7924" data-mp="h4vko0e2sd7s">{post.flagged}</span>
                </span>
                <Button size="sm" variant="secondary" data-id="element-7925" data-mp="7cy40e4ast1o">
                  Review
                </Button>
              </li>)}
            {draftEvents.map(event => <li key={event.id} className="flex flex-wrap items-center gap-3 py-3" data-id="element-7926" data-mp="2b8tr0owgjen">
                <CalendarIcon aria-hidden className="h-5 w-5 shrink-0 text-ink-muted" data-id="element-7927" data-mp="3tm0r6lv70bxk" />
                <span className="min-w-0 flex-1" data-id="element-7928" data-mp="3pikxnyef6rp3">
                  <span className="block text-body font-medium text-ink" data-id="element-7929" data-mp="3kjrhtzbnuolr">
                    {event.title} is still a draft
                  </span>
                  <span className="block text-caption text-ink-muted" data-id="element-7930" data-mp="1nq6gj1jp9r9q">
                    {event.date} · nobody can register until it is published.
                  </span>
                </span>
                <Button size="sm" variant="secondary" data-id="element-7931" data-mp="1qb455p0dhv1s">
                  Publish
                </Button>
              </li>)}
          </ul>}
      </Panel>

      <div className="mt-6 grid gap-4 lg:grid-cols-2" data-id="element-7932" data-mp="2028las1sbly7">
        <Panel title="How the society is doing" data-id="element-7933" data-mp="250adc2h6uvlt">
          <ul className="space-y-4" data-id="element-7934" data-mp="24yz0ze9ogfj3">
            {communityMetrics.map(metric => <li key={metric.label} data-id="element-7935" data-mp="17djqfarbocuw">
                <p className="flex items-baseline justify-between gap-3" data-id="element-7936" data-mp="17emj9o87gpdg">
                  <span className="text-body-sm text-ink-muted" data-id="element-7937" data-mp="1cc4tdhxylan1">{metric.label}</span>
                  <span className="cz-num text-h4 font-semibold text-ink" data-id="element-7938" data-mp="1h9mq89ypbzeo">{metric.value}</span>
                </p>
                <p className="mt-1 text-caption leading-snug text-ink-subtle" data-id="element-7939" data-mp="1ldujmkg3u3j8">{metric.note}</p>
              </li>)}
          </ul>
        </Panel>

        <Panel title="Next up" data-id="element-7940" data-mp="2wr6j6j4tzzzl">
          <ul className="space-y-3" data-id="element-7941" data-mp="2wq53g1cvepbj">
            {communityEvents.filter(event => event.state !== 'draft').map(event => <li key={event.id} className="flex gap-3" data-id="element-7942" data-mp="snvdupr3o5jy">
                  <img src={event.image} alt="" loading="lazy" className="h-16 w-24 shrink-0 rounded-lg object-cover" data-id="element-7943" data-mp="snbz1hwqxxa5" />
                  <span className="min-w-0" data-id="element-7944" data-mp="11mgvmioqzgjo">
                    <span className="block text-body font-medium text-ink" data-id="element-7945" data-mp="dzqvjmvylw1p">{event.title}</span>
                    <span className="block text-caption text-ink-muted" data-id="element-7946" data-mp="9xwzjrwplg7v">
                      {event.date} · {event.venue}
                    </span>
                    <span className="mt-1 block" data-id="element-7947" data-mp="nqd44x59dmaw">
                      <Meter value={Math.round(event.registered / event.capacity * 100)} label={`${event.registered} of ${event.capacity} places taken`} data-id="element-7948" data-mp="iwr0fuh2osr8" />
                    </span>
                  </span>
                </li>)}
          </ul>
        </Panel>
      </div>
    </>;
}

/* ------------------------------------------------------------------ members */

function Members() {
  const [filter, setFilter] = useState<'all' | MemberState>('all');
  const shown = filter === 'all' ? communityMembers : communityMembers.filter(m => m.state === filter);
  return <>
      <PageHeader title="Members" lead="Everyone who joined this society, and the role each one holds here. A role in this community grants nothing anywhere else on Campuzon." data-id="element-7949" data-mp="2lkh6i4q46iu3" />
      <DemoNote data-id="element-7950" data-mp="2bm9qpp7ka6to" />

      <div className="mt-6 flex flex-wrap gap-2" data-id="element-7951" data-mp="2bmt3kricu50u">
        {(['all', 'active', 'pending', 'invited', 'suspended'] as const).map(state => <button key={state} type="button" onClick={() => setFilter(state)} aria-pressed={filter === state} className={`min-h-[44px] rounded-full border px-4 text-body-sm font-medium capitalize transition-colors duration-150 ease-out ${filter === state ? 'border-brand-700 bg-brand-700 text-white' : 'border-line bg-surface text-ink hover:bg-canvas'}`} data-id="element-7952" data-mp="2gjjg54uyaihi">
            {state}
          </button>)}
      </div>

      <Panel className="mt-4" bodyClassName="p-0" data-id="element-7953" data-mp="1hx1du8zv0736">
        {shown.length === 0 ? <EmptyState icon={<UsersIcon className="h-5 w-5" data-id="element-7955" data-mp="2fkjm7iplz76n" />} title="No members in this state" description="Change the filter above to see the rest of the society." data-id="element-7954" data-mp="2fka38f2ze0ag" /> : <ul className="divide-y divide-line" data-id="element-7956" data-mp="25tox86kz9thi">
            {shown.map(member => <li key={member.id} className="flex flex-wrap items-center gap-3 p-4" data-id="element-7957" data-mp="20vx97nngeril">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-50 text-body-sm font-semibold text-brand-700" data-id="element-7958" data-mp="2yahspudrcs86">
                  {member.name.split(' ').map(part => part[0]).join('')}
                </span>
                <span className="min-w-0 flex-1" data-id="element-7959" data-mp="35tr4tvdnf50x">
                  <span className="block text-body font-medium text-ink" data-id="element-7960" data-mp="35wd8hjxf50dv">{member.name}</span>
                  <span className="block text-caption text-ink-muted" data-id="element-7961" data-mp="3as2d41oap7h1">
                    {member.handle} · {member.programme}
                  </span>
                  <span className="mt-1 block text-caption text-ink-subtle" data-id="element-7962" data-mp="3ks2vnlecbjk6">
                    {member.standing} · joined {member.joined} · last active {member.lastActive}
                  </span>
                </span>
                <span className="flex items-center gap-2" data-id="element-7963" data-mp="3tl68brh5vsdr">
                  <Badge tone={MEMBER_TONE[member.state]} data-id="element-7964" data-mp="2dajcokce6xj">{member.state}</Badge>
                  <span className="text-body-sm text-ink-muted" data-id="element-7965" data-mp="2felb0yokvv6">{member.role}</span>
                </span>
              </li>)}
          </ul>}
      </Panel>

      <p className="mt-4 flex items-start gap-2 rounded-xl border border-line bg-canvas p-3 text-caption text-ink-muted" data-id="element-7966" data-mp="cc9kzn6ox2b1">
        <InfoIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-7967" data-mp="2ca9hh80a8rgj" />
        <span data-id="element-7968" data-mp="2ge7hrm1cg8b6">
          You see a member’s academic standing because it is how people know who they are dealing
          with. Their results, notes, messages, wellbeing records and balances are not part of this
          record and no community role can reach them.
        </span>
      </p>
    </>;
}

/* -------------------------------------------------------------------- posts */

function Posts() {
  return <>
      <PageHeader title="Posts" lead="Everything published into this community. A removed post keeps its place with the reason stated, rather than disappearing." data-id="element-7969" data-mp="303rlwj6hkscv" />
      <DemoNote data-id="element-7970" data-mp="391k4bw7i8g4h" />
      <div className="mt-6 space-y-3" data-id="element-7971" data-mp="38zz904dd8t5k">
        {communityPosts.map(post => <Panel key={post.id} data-id="element-7972" data-mp="19r26tn74pwd2">
            <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-7973" data-mp="1eo2eu3njh96t">
              <div className="min-w-0" data-id="element-7974" data-mp="1eq5dtd6o6cno">
                <p className="text-body-sm font-medium text-ink" data-id="element-7975" data-mp="1752unvs97dme">{post.author}</p>
                <p className="text-caption text-ink-subtle" data-id="element-7976" data-mp="12ecqglvkdgta">
                  {post.when} · {post.audience}
                </p>
              </div>
              <Badge tone={post.state === 'needs review' ? 'danger' : post.state === 'published' ? 'ok' : 'neutral'} data-id="element-7977" data-mp="xfja8low7bpf">
                {post.state}
              </Badge>
            </div>
            <p className="mt-3 text-body text-ink" data-id="element-7978" data-mp="1g5hhx0os32sj">{post.excerpt}</p>
            {post.flagged && <p className="mt-3 flex items-start gap-2 rounded-lg border border-danger-100 bg-danger-50 p-3 text-caption text-ink" data-id="element-7979" data-mp="1c3wavvyxdiq9">
                <AlertTriangleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-danger-700" data-id="element-7980" data-mp="4rdqa15nxqqn" />
                <span data-id="element-7981" data-mp="no39g4mzh7q">{post.flagged}</span>
              </p>}
            <div className="mt-3 flex flex-wrap items-center gap-4 text-caption text-ink-muted" data-id="element-7982" data-mp="2662p0lyw5gr">
              <span className="cz-num" data-id="element-7983" data-mp="74pmovh0376h">{post.reactions} reactions</span>
              <span className="cz-num" data-id="element-7984" data-mp="72mzej78fs5g">{post.comments} comments</span>
              {post.state === 'needs review' && <span className="ml-auto flex gap-2" data-id="element-7985" data-mp="3tbxmzjd2cnvn">
                  <Button size="sm" variant="secondary" icon={<XIcon className="h-4 w-4" data-id="element-7987" data-mp="3fl2ngxogfj4q" />} data-id="element-7986" data-mp="3tammptitty9a">
                    Remove with a reason
                  </Button>
                  <Button size="sm" variant="primary" icon={<CheckIcon className="h-4 w-4" data-id="element-7989" data-mp="3kcu52ipmueiq" />} data-id="element-7988" data-mp="3kbrd07gkq618">
                    Keep it up
                  </Button>
                </span>}
            </div>
          </Panel>)}
      </div>
    </>;
}

/* ------------------------------------------------------------ announcements */

function Announcements() {
  return <>
      <PageHeader title="Announcements" lead="Sent to members through their existing Campuzon notification preferences. Muting a topic stops the email, never the record." data-id="element-7990" data-mp="30o1t0mevpa2k" />
      <DemoNote data-id="element-7991" data-mp="2d0epw3e3ore4" />
      <div className="mt-6 space-y-3" data-id="element-7992" data-mp="2d0wx1v2awq0t">
        {announcements.map(item => <Panel key={item.id} title={item.title} data-id="element-7993" data-mp="2qzo2kt33l3dc">
            <p className="text-body text-ink" data-id="element-7994" data-mp="2r17n4vvse0kx">{item.body}</p>
            <dl className="mt-4 grid gap-4 sm:grid-cols-3" data-id="element-7995" data-mp="2m0jwibbgzlr8">
              <div data-id="element-7996" data-mp="2m362i6ippt7c">
                <dt className="text-caption text-ink-muted" data-id="element-7997" data-mp="30xu7ljk7apmf">Audience</dt>
                <dd className="text-body-sm font-medium text-ink" data-id="element-7998" data-mp="30vr6o4hifc2x">{item.audience}</dd>
              </div>
              <div data-id="element-7999" data-mp="2vx6emscevypk">
                <dt className="text-caption text-ink-muted" data-id="element-8000" data-mp="39q5syr0gjmvj">Reached</dt>
                <dd className="cz-num text-body-sm font-medium text-ink" data-id="element-8001" data-mp="39s8k5edcjm1u">
                  {item.reach.toLocaleString()}
                </dd>
              </div>
              <div data-id="element-8002" data-mp="3jpda7pabc1nx">
                <dt className="text-caption text-ink-muted" data-id="element-8003" data-mp="3jov2nwi7a10p">Opened</dt>
                <dd className="cz-num text-body-sm font-medium text-ink" data-id="element-8004" data-mp="3eqjwpwv10anr">
                  {item.opened.toLocaleString()}
                </dd>
              </div>
            </dl>
            <p className="mt-3 text-caption text-ink-subtle" data-id="element-8005" data-mp="6nauurgska22">Sent {item.sent}</p>
          </Panel>)}
      </div>
      <div className="mt-4" data-id="element-8006" data-mp="bfkh9xip6rzl">
        <Button variant="primary" icon={<MegaphoneIcon className="h-4 w-4" data-id="element-8008" data-mp="gdchrfxermfe" />} data-id="element-8007" data-mp="bgm22tpimsui">
          Write an announcement
        </Button>
      </div>
    </>;
}

/* ------------------------------------------------------------------- events */

function Events() {
  return <>
      <PageHeader title="Events" lead="Events created here appear on the campus events surface. Ticketed events settle through the marketplace, not through this console." data-id="element-8009" data-mp="3jv43wdov5c5o" />
      <DemoNote data-id="element-8010" data-mp="3ttbiipvoji4i" />
      <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-8011" data-mp="3tttncawer4o1">
        {communityEvents.map(event => <article key={event.id} className="overflow-hidden rounded-2xl border border-line bg-surface" data-id="element-8012" data-mp="145atu24olltl">
            <img src={event.image} alt="" loading="lazy" className="h-40 w-full object-cover" data-id="element-8013" data-mp="2tkvdc5f3t7xw" />
            <div className="flex flex-col p-4" data-id="element-8014" data-mp="2onle5y1wxhwv">
              <div className="flex items-start justify-between gap-2" data-id="element-8015" data-mp="2jth61bgwv3u7">
                <h3 className="text-body font-semibold text-ink" data-id="element-8016" data-mp="2evpb08kyz1ls">{event.title}</h3>
                <Badge tone={event.state === 'open' ? 'ok' : event.state === 'full' ? 'warn' : 'neutral'} data-id="element-8017" data-mp="2atudlf65t8da">
                  {event.state}
                </Badge>
              </div>
              <p className="mt-1 text-caption text-ink-muted" data-id="element-8018" data-mp="20wg11qncx3q9">
                {event.date} · {event.venue}
              </p>
              <p className="mt-3" data-id="element-8019" data-mp="1vyo94wmpphm2">
                <Meter value={Math.round(event.registered / event.capacity * 100)} label={`${event.registered} of ${event.capacity} places taken`} data-id="element-8020" data-mp="3va7gj6sdahtm" />
              </p>
              <p className="mt-auto pt-3 text-caption text-ink-subtle" data-id="element-8021" data-mp="1okdtxthsuwym">Ticket: {event.ticket}</p>
            </div>
          </article>)}
      </div>
    </>;
}

/* ------------------------------------------------------------ registrations */

function Registrations() {
  return <>
      <PageHeader title="Registrations" lead="Who signed up, and what actually happened. Registration and attendance are kept apart because they are rarely the same number." data-id="element-8022" data-mp="1jndonksrxy8k" />
      <DemoNote data-id="element-8023" data-mp="2c9yfmk5c88bl" />
      <Panel className="mt-6" bodyClassName="p-0" data-id="element-8024" data-mp="2c9olckd9ff03">
        <ul className="divide-y divide-line" data-id="element-8025" data-mp="1ybi9aube0jtz">
          {registrations.map(entry => <li key={entry.id} className="flex flex-wrap items-center gap-3 p-4" data-id="element-8026" data-mp="22ctp684glqoz">
              <ClipboardListIcon aria-hidden className="h-5 w-5 shrink-0 text-ink-muted" data-id="element-8027" data-mp="22ewnrgjogs5b" />
              <span className="min-w-0 flex-1" data-id="element-8028" data-mp="my61y9gf5j32">
                <span className="block text-body font-medium text-ink" data-id="element-8029" data-mp="qxx04y89z1hm">{entry.name}</span>
                <span className="block text-caption text-ink-muted" data-id="element-8030" data-mp="jdme4w33u88q">
                  {entry.handle} · {entry.event}
                </span>
              </span>
              <span className="text-caption text-ink-subtle" data-id="element-8031" data-mp="eh5fv926ydhx">{entry.when}</span>
              <Badge tone={entry.status === 'confirmed' ? 'ok' : entry.status === 'waitlisted' ? 'warn' : 'neutral'} data-id="element-8032" data-mp="tbti8ecaovtb">
                {entry.status}
              </Badge>
            </li>)}
        </ul>
      </Panel>
    </>;
}

/* -------------------------------------------------------------------- polls */

function Polls() {
  return <>
      <PageHeader title="Polls" lead="One vote per identity — a second attempt is refused rather than re-counted. An anonymous poll cannot be traced back to a voter, including by you." data-id="element-8033" data-mp="5lfzxfjiibbx" />
      <DemoNote data-id="element-8034" data-mp="391d8xl5xtd8o" />
      <div className="mt-6 space-y-3" data-id="element-8035" data-mp="2nk4q6tirfqxl">
        {polls.map(poll => {
        const total = poll.options.reduce((sum, option) => sum + option.count, 0) || 1;
        return <Panel key={poll.id} title={poll.question} data-id="element-8036" data-mp="2slbz4vwn2w5u">
              <div className="flex flex-wrap items-center gap-2" data-id="element-8037" data-mp="2xh9cqugipoex">
                <Badge tone={poll.state === 'open' ? 'ok' : 'neutral'} data-id="element-8038" data-mp="31jnxxl7nt09d">{poll.state}</Badge>
                {poll.anonymous && <Badge plain data-id="element-8039" data-mp="36jip7pvcjruy">Anonymous</Badge>}
                <span className="text-caption text-ink-muted" data-id="element-8040" data-mp="36iqro9i6vk21">{poll.closes}</span>
              </div>
              <ul className="mt-4 space-y-3" data-id="element-8041" data-mp="3bg9449vf0hes">
                {poll.options.map(option => <li key={option.label} data-id="element-8042" data-mp="3g9lpmb4ss83b">
                    <p className="flex items-baseline justify-between gap-3 text-body-sm text-ink" data-id="element-8043" data-mp="3l7n7wtueg3dq">
                      <span data-id="element-8044" data-mp="3p9rwvf9wkbu1">{option.label}</span>
                      <span className="cz-num text-ink-muted" data-id="element-8045" data-mp="3pb309dzfrhkm">
                        {Math.round(option.count / total * 100)}%
                      </span>
                    </p>
                    <Meter value={Math.round(option.count / total * 100)} label={`${option.count} votes`} data-id="element-8046" data-mp="1im13svzp528i" />
                  </li>)}
              </ul>
              <p className="mt-3 cz-num text-caption text-ink-subtle" data-id="element-8047" data-mp="19mnsryvxc5lo">{poll.votes} votes cast</p>
            </Panel>;
      })}
      </div>
      <div className="mt-4" data-id="element-8048" data-mp="21bz9j5g0uj8z">
        <Button variant="primary" icon={<VoteIcon className="h-4 w-4" data-id="element-8050" data-mp="21dk42v2c626u" />} data-id="element-8049" data-mp="21b7r6wbfqi0n">
          Create a poll
        </Button>
      </div>
    </>;
}

/* -------------------------------------------------------------------- media */

function Media() {
  return <>
      <PageHeader title="Media" lead="Photographs and files attached to this society’s events and posts." data-id="element-8051" data-mp="1nkuiusumt1as" />
      <DemoNote data-id="element-8052" data-mp="2l0i4k3npkqvl" />
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-8053" data-mp="2l11ccrjn9yvw">
        {communityEvents.map(event => <figure key={event.id} className="overflow-hidden rounded-2xl border border-line" data-id="element-8054" data-mp="2g75hvz2lau5h">
            <img src={event.image} alt="" loading="lazy" className="h-44 w-full object-cover" data-id="element-8055" data-mp="2xu5k2q4tsx5o" />
            <figcaption className="bg-surface p-3 text-caption text-ink-muted" data-id="element-8056" data-mp="2sz8exuao37y2">
              {event.title}
            </figcaption>
          </figure>)}
      </div>
      <p className="mt-4 flex items-start gap-2 rounded-xl border border-line bg-canvas p-3 text-caption text-ink-muted" data-id="element-8057" data-mp="2j09bqr8o84ig">
        <ImageIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-8058" data-mp="3gosx4ayzy16a" />
        <span data-id="element-8059" data-mp="3boy687f81bl8">
          Media uploaded here belongs to the society, not to the individual who uploaded it. A
          member leaving does not remove the society’s event photographs.
        </span>
      </p>
    </>;
}

/* ----------------------------------------------------------------- settings */

function Settings() {
  return <>
      <PageHeader title="Settings" lead="What this society is, who runs it, and what its recognition actually means." data-id="element-8060" data-mp="39objkdnv8nh" />
      <DemoNote data-id="element-8061" data-mp="6qsraevpyapw" />
      <div className="mt-6 grid gap-4 lg:grid-cols-2" data-id="element-8062" data-mp="6r2fbwx7e7sy">
        <Panel title="Society" data-id="element-8063" data-mp="ar2trhkayj00">
          <dl className="space-y-3" data-id="element-8064" data-mp="as47xuwm7lly">
            <Row label="Name" value={communityProfile.name} data-id="element-8065" data-mp="atfe21dj95g8" />
            <Row label="Kind" value={communityProfile.kind} data-id="element-8066" data-mp="fpny9t6l7ou9" />
            <Row label="Institution" value={communityProfile.institution} data-id="element-8067" data-mp="kp8ys81eo593" />
            <Row label="Campus" value={communityProfile.campus} data-id="element-8068" data-mp="kn5xgrusypp2" />
            <Row label="Community manager" value={`${communityProfile.leadName} · ${communityProfile.leadRole}`} data-id="element-8069" data-mp="pntupv9mk2rq" />
          </dl>
        </Panel>
        <Panel title="Recognition" data-id="element-8070" data-mp="yhyl6dkujc6q">
          <p className="flex items-start gap-2" data-id="element-8071" data-mp="yj075d3ehj38">
            <BadgeCheckIcon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-ok-700" data-id="element-8072" data-mp="13g9t2irkzcmb" />
            <span className="text-body text-ink" data-id="element-8073" data-mp="18fmq1wc6202m">{communityProfile.recognitionNote}</span>
          </p>
          <p className="mt-4 text-caption text-ink-muted" data-id="element-8074" data-mp="1ddo6sam4dnax">
            Recognition is granted by the institution and confirmed by Campuzon. It cannot be
            changed from this console — which is the point of it meaning anything.
          </p>
        </Panel>
      </div>
      <Panel title="What this console cannot reach" className="mt-4" data-id="element-8075" data-mp="xfvdd9rg285e">
        <ul className="space-y-2 text-body-sm text-ink-muted" data-id="element-8076" data-mp="xh6gr8gz9dvz">
          {['Private messages between members', 'Notes, diary entries and wellbeing records', 'Academic results and attendance', 'Balances, orders and payment details', 'Roommate preferences and approximate location'].map(item => <li key={item} className="flex items-start gap-2" data-id="element-8077" data-mp="1gdeoqk1dk2uz">
              <XIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-8078" data-mp="1bfwf0rfj9jlx" />
              <span data-id="element-8079" data-mp="3i62wn6oyreh3">{item}</span>
            </li>)}
        </ul>
        <p className="mt-3 text-caption text-ink-subtle" data-id="element-8080" data-mp="3i6cjilepp5ik">
          These are absent from the records this workspace can read, rather than hidden by this
          screen.
        </p>
      </Panel>
    </>;
}

/* ----------------------------------------------------------------- fragments */

function Row({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-8081" data-mp="1iycoa2jl5r1t">
      <dt className="text-body-sm text-ink-muted" data-id="element-8082" data-mp="1nxp7mdb90fzn">{label}</dt>
      <dd className="text-body-sm font-medium text-ink" data-id="element-8083" data-mp="1nw5vmywmkr3o">{value}</dd>
    </div>;
}
function DemoNote() {
  return <p className="mt-4 flex items-start gap-2 rounded-xl border border-line bg-canvas p-3 text-caption text-ink-muted" data-id="element-8084" data-mp="1e02spqty8ofo">
      <InfoIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-8085" data-mp="100tfn011iafx" />
      <span data-id="element-8086" data-mp="102mlid3twqmv">
        Demonstration records for a student society — not a Campuzon claim about a real
        organisation.
      </span>
    </p>;
}